import React, { useRef, useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import { api, type Vivienda } from '@/lib/api';

declare const maplibregl: typeof import('maplibre-gl');

const ESTADO_COLOR: Record<string, string> = {
  construccion: '#f59e0b',
  lista: '#3b82f6',
  entregada: '#22c55e',
};

const ESTADO_LABEL: Record<string, string> = {
  construccion: 'En construcción',
  lista: 'Lista',
  entregada: 'Entregada',
};

const ESTADO_ICON: Record<string, string> = {
  construccion: '🏗️',
  lista: '🔑',
  entregada: '🏠',
};

function buildPrintContent(viviendas: Vivienda[], filtroProyecto: string, filtroEstado: string) {
  const now = new Date().toLocaleDateString('es-VE', { day: '2-digit', month: 'long', year: 'numeric' });
  const construccion = viviendas.filter(v => v.estado === 'construccion').length;
  const listas = viviendas.filter(v => v.estado === 'lista').length;
  const entregadas = viviendas.filter(v => v.estado === 'entregada').length;

  return `
    <div id="viv-pdf-root" style="font-family:sans-serif;padding:24px;color:#111">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #1d4ed8;padding-bottom:12px;margin-bottom:20px">
        <div>
          <div style="font-size:20px;font-weight:800;color:#1d4ed8">Módulo de Viviendas</div>
          <div style="font-size:12px;color:#555;margin-top:2px">Reporte generado el ${now}</div>
        </div>
        <div style="text-align:right;font-size:11px;color:#555">
          ${filtroProyecto ? `Proyecto: <b>${filtroProyecto}</b><br>` : ''}
          ${filtroEstado ? `Estado: <b>${ESTADO_LABEL[filtroEstado] ?? filtroEstado}</b>` : 'Todos los estados'}
        </div>
      </div>

      <div style="display:flex;gap:16px;margin-bottom:20px">
        ${[
          { label: 'Total', valor: viviendas.length, color: '#1d4ed8' },
          { label: 'En construcción', valor: construccion, color: '#d97706' },
          { label: 'Listas', valor: listas, color: '#2563eb' },
          { label: 'Entregadas', valor: entregadas, color: '#16a34a' },
        ].map(c => `
          <div style="flex:1;border:1px solid #e5e7eb;border-radius:8px;padding:12px;text-align:center">
            <div style="font-size:22px;font-weight:800;color:${c.color}">${c.valor}</div>
            <div style="font-size:10px;color:#6b7280">${c.label}</div>
          </div>
        `).join('')}
      </div>

      <table style="width:100%;border-collapse:collapse;font-size:11px">
        <thead>
          <tr style="background:#1d4ed8;color:#fff">
            <th style="padding:8px;text-align:left">Proyecto</th>
            <th style="padding:8px;text-align:left">Dirección</th>
            <th style="padding:8px;text-align:left">Unidad</th>
            <th style="padding:8px;text-align:left">Estado</th>
            <th style="padding:8px;text-align:left">Beneficiario</th>
            <th style="padding:8px;text-align:left">Cédula</th>
            <th style="padding:8px;text-align:left">Teléfono</th>
            <th style="padding:8px;text-align:center">Familias</th>
            <th style="padding:8px;text-align:left">Entrega</th>
          </tr>
        </thead>
        <tbody>
          ${viviendas.map((v, i) => `
            <tr style="background:${i % 2 ? '#f9fafb' : '#fff'}">
              <td style="padding:7px 8px;font-weight:600">${v.proyecto}</td>
              <td style="padding:7px 8px">${v.direccion}</td>
              <td style="padding:7px 8px">${[v.bloque ? `Bl. ${v.bloque}` : '', v.piso ? `P. ${v.piso}` : '', v.apartamento ? `Ap. ${v.apartamento}` : ''].filter(Boolean).join(' ')}</td>
              <td style="padding:7px 8px">
                <span style="background:${ESTADO_COLOR[v.estado]}22;color:${ESTADO_COLOR[v.estado]};padding:2px 8px;border-radius:99px;font-weight:700">${ESTADO_LABEL[v.estado]}</span>
              </td>
              <td style="padding:7px 8px">${v.benefNombre ?? '—'}</td>
              <td style="padding:7px 8px">${v.benefCedula ?? '—'}</td>
              <td style="padding:7px 8px">${v.benefTelefono ?? '—'}</td>
              <td style="padding:7px 8px;text-align:center">${v.benefFamilias ?? '—'}</td>
              <td style="padding:7px 8px">${v.fechaEntrega ? new Date(v.fechaEntrega).toLocaleDateString('es-VE') : '—'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  `;
}

export default function ViviendaAdminView() {
  const { token } = useAuthStore();
  const qc = useQueryClient();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const [selected, setSelected] = useState<Vivienda | null>(null);
  const [filtroProyecto, setFiltroProyecto] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [editEstado, setEditEstado] = useState<string>('');

  const params: Record<string, string> = {};
  if (filtroProyecto) params.proyecto = filtroProyecto;
  if (filtroEstado) params.estado = filtroEstado;

  const { data: viviendas = [] } = useQuery<Vivienda[]>({
    queryKey: ['viviendas', filtroProyecto, filtroEstado],
    queryFn: () => api.viviendas(token!, params),
    enabled: !!token,
  });

  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Vivienda> }) =>
      api.patchVivienda(token!, id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['viviendas'] }),
  });

  const proyectos = [...new Set(viviendas.map(v => v.proyecto))];

  // Map
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    mapInstance.current = new maplibregl.Map({
      container: mapRef.current!,
      style: 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',
      center: [-66.9146, 10.4806],
      zoom: 13,
    });
    return () => { mapInstance.current?.remove(); mapInstance.current = null; };
  }, []);

  useEffect(() => {
    if (!mapInstance.current) return;
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    viviendas.forEach(v => {
      if (v.lat == null || v.lng == null) return;
      const el = document.createElement('div');
      el.style.cssText = `width:20px;height:20px;border-radius:50%;background:${ESTADO_COLOR[v.estado]};border:2px solid #fff;cursor:pointer;box-shadow:0 2px 6px rgba(0,0,0,.4)`;
      el.title = v.proyecto;
      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([v.lng, v.lat])
        .addTo(mapInstance.current!);
      el.addEventListener('click', () => setSelected(v));
      markersRef.current.push(marker);
    });
  }, [viviendas]);

  function exportarPDF() {
    const win = window.open('', '_blank')!;
    win.document.write(`<!DOCTYPE html><html><head><title>Viviendas</title>
      <style>body{margin:0;padding:0}@media print{}</style>
      </head><body>${buildPrintContent(viviendas, filtroProyecto, filtroEstado)}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  }

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', background: '#0a1628' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px 12px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: '#60a5fa' }}>🏠 Módulo de Viviendas</div>
        <div style={{ flex: 1 }} />
        {/* Counters */}
        {(['construccion', 'lista', 'entregada'] as const).map(e => (
          <div key={e} style={{ display: 'flex', alignItems: 'center', gap: 5, background: `${ESTADO_COLOR[e]}18`, borderRadius: 8, padding: '4px 10px' }}>
            <span style={{ fontSize: 13 }}>{ESTADO_ICON[e]}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: ESTADO_COLOR[e] }}>{viviendas.filter(v => v.estado === e).length}</span>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{ESTADO_LABEL[e]}</span>
          </div>
        ))}
        <button onClick={exportarPDF} style={{ background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
          <i className="ti ti-file-download" /> Exportar PDF
        </button>
      </div>

      {/* Filters */}
      <div style={{ padding: '10px 20px', display: 'flex', gap: 10, borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
        <select value={filtroProyecto} onChange={e => setFiltroProyecto(e.target.value)}
          style={{ background: 'rgba(255,255,255,0.07)', color: '#fff', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '5px 10px', fontSize: 12 }}>
          <option value="">Todos los proyectos</option>
          {proyectos.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
          style={{ background: 'rgba(255,255,255,0.07)', color: '#fff', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '5px 10px', fontSize: 12 }}>
          <option value="">Todos los estados</option>
          <option value="construccion">En construcción</option>
          <option value="lista">Lista</option>
          <option value="entregada">Entregada</option>
        </select>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', alignSelf: 'center', marginLeft: 4 }}>{viviendas.length} viviendas</div>
      </div>

      {/* Body: map + list */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Map */}
        <div ref={mapRef} style={{ flex: 1, minWidth: 0 }} />

        {/* List */}
        <div style={{ width: 340, borderLeft: '1px solid rgba(255,255,255,0.07)', overflowY: 'auto', flexShrink: 0 }}>
          {viviendas.map(v => (
            <div key={v.id} onClick={() => setSelected(selected?.id === v.id ? null : v)}
              style={{ padding: '11px 14px', borderBottom: '0.5px solid rgba(255,255,255,0.06)', cursor: 'pointer', background: selected?.id === v.id ? 'rgba(96,165,250,0.1)' : 'transparent', transition: 'background 0.15s' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: ESTADO_COLOR[v.estado], flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.proyecto}</div>
                  <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.45)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.direccion}</div>
                </div>
                <span style={{ fontSize: 9.5, fontWeight: 700, color: ESTADO_COLOR[v.estado], background: `${ESTADO_COLOR[v.estado]}18`, borderRadius: 6, padding: '2px 6px', flexShrink: 0 }}>{ESTADO_LABEL[v.estado]}</span>
              </div>
              {v.benefNombre && (
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 4, paddingLeft: 18 }}>👤 {v.benefNombre}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Detail panel */}
      {selected && (
        <div style={{ position: 'absolute', bottom: 20, right: 360, width: 320, background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(20px)', borderRadius: 14, border: '1px solid rgba(255,255,255,0.12)', padding: 18, zIndex: 30, animation: 'popIn 200ms ease-out' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>{selected.proyecto}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>{selected.direccion}</div>
            </div>
            <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
          </div>

          {/* Unidad */}
          {(selected.bloque || selected.piso || selected.apartamento) && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              {selected.bloque && <Tag label="Bloque" value={selected.bloque} />}
              {selected.piso && <Tag label="Piso" value={selected.piso} />}
              {selected.apartamento && <Tag label="Apto" value={selected.apartamento} />}
            </div>
          )}

          {/* Estado selector */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>ESTADO</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {(['construccion', 'lista', 'entregada'] as const).map(e => (
                <button key={e} onClick={() => {
                  patchMut.mutate({ id: selected.id, body: { estado: e } });
                  setSelected({ ...selected, estado: e });
                }} style={{ flex: 1, fontSize: 10, fontWeight: 700, padding: '5px 0', borderRadius: 7, border: `1.5px solid ${ESTADO_COLOR[e]}`, background: selected.estado === e ? ESTADO_COLOR[e] : 'transparent', color: selected.estado === e ? '#fff' : ESTADO_COLOR[e], cursor: 'pointer' }}>
                  {ESTADO_ICON[e]} {ESTADO_LABEL[e]}
                </button>
              ))}
            </div>
          </div>

          {/* Beneficiary */}
          <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#60a5fa', marginBottom: 8 }}>DATOS DEL BENEFICIARIO</div>
            <InfoRow icon="👤" label="Nombre" value={selected.benefNombre} />
            <InfoRow icon="🪪" label="Cédula" value={selected.benefCedula} />
            <InfoRow icon="📱" label="Teléfono" value={selected.benefTelefono} />
            <InfoRow icon="👨‍👩‍👧‍👦" label="Familias" value={selected.benefFamilias?.toString()} />
            {selected.fechaEntrega && (
              <InfoRow icon="📅" label="Entrega" value={new Date(selected.fechaEntrega).toLocaleDateString('es-VE')} />
            )}
          </div>

          {selected.registradoPor && (
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginTop: 8 }}>Registrado por: {selected.registradoPor.nombre}</div>
          )}
        </div>
      )}
    </div>
  );
}

function Tag({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ fontSize: 10, background: 'rgba(255,255,255,0.08)', borderRadius: 6, padding: '3px 8px', color: 'rgba(255,255,255,0.6)' }}>
      <span style={{ color: 'rgba(255,255,255,0.35)' }}>{label}: </span>{value}
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: string; label: string; value?: string }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 5, fontSize: 11 }}>
      <span>{icon}</span>
      <span style={{ color: 'rgba(255,255,255,0.4)' }}>{label}:</span>
      <span style={{ color: '#fff', fontWeight: 600 }}>{value}</span>
    </div>
  );
}
